import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import * as atlas from 'azure-maps-control';
import { Card, CardDescription, CardTitle } from '@/components/ui/card';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { ErrorState } from '@/components/common/ErrorState';
import { mapAuthOptions, useMapsConfig, useMapStyle } from '@/lib/maps';
import { FRESH_DAYS, agoLabel, isStale } from '../lib/ais';
import { useRigsMap, useWells } from '../lib/api';
import type { RigType, WellDto, WellStatus } from '../types';
import { ConfidenceValue, SourceLink, StatusDot, WELL_STATUS_COLORS, WELL_STATUS_LABELS } from './_shared';

// Tab 2 — Map. ONE Azure Maps canvas (owner directive: real basemap, no hand-drawn maps)
// with two tabs that swap the visible layer set:
//   Wells — tracked offshore wells in the semantic status colors; approximate (model-derived)
//           positions render hollow, exactly like the retired canvas map.
//   Rigs  — rigs with a scraped AIS position (/api/rigs/map). Solid = fresh AIS message,
//           hollow = stale (older than FRESH_DAYS) or undated. Positions are aggregator-
//           sourced 'reported' data (ADR-0007 advisory) — the provider is attributed in the
//           popup and the detail line.
// Click: wells pin a detail card below the canvas; rigs deep-link to the spec modal.

const RIG_TYPE_COLORS: Record<RigType, string> = {
  drillship: '#3b82f6',
  semisub: '#8b5cf6',
  jackup: '#f59e0b',
  platform: '#64748b',
  tender_assist: '#14b8a6',
  spar: '#ec4899',
};

const RIG_TYPE_LABELS: Record<RigType, string> = {
  drillship: 'Drillship',
  semisub: 'Semisub',
  jackup: 'Jackup',
  platform: 'Platform',
  tender_assist: 'Tender assist',
  spar: 'Spar',
};

/** Popup content built with DOM APIs (textContent) — marker fields are data, never markup. */
function popupContent(lines: { text: string; strong?: boolean; muted?: boolean }[]): HTMLElement {
  const root = document.createElement('div');
  root.style.cssText = 'padding:8px 12px;font-size:12px;line-height:1.45;max-width:240px;color:#1a1a1d;';
  for (const l of lines) {
    const div = document.createElement('div');
    div.textContent = l.text;
    if (l.strong) div.style.fontWeight = '600';
    if (l.muted) div.style.opacity = '0.65';
    root.appendChild(div);
  }
  return root;
}

type MapTab = 'wells' | 'rigs';

export default function FleetMap() {
  const configQuery = useMapsConfig();
  const wellsQuery = useWells();
  const rigsQuery = useRigsMap();
  const mapStyle = useMapStyle();
  const navigate = useNavigate();

  const [tab, setTab] = useState<MapTab>('wells');
  const [mapReady, setMapReady] = useState(false);
  const [selectedWell, setSelectedWell] = useState<WellDto | null>(null);

  const mapElRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<atlas.Map | null>(null);
  const wellsSourceRef = useRef<atlas.source.DataSource | null>(null);
  const rigsSourceRef = useRef<atlas.source.DataSource | null>(null);
  const wellsLayerRef = useRef<atlas.layer.BubbleLayer | null>(null);
  const rigsLayerRef = useRef<atlas.layer.BubbleLayer | null>(null);
  const popupRef = useRef<atlas.Popup | null>(null);

  const wells = useMemo(() => wellsQuery.data?.wells ?? [], [wellsQuery.data]);
  // The map-creation effect runs once; its click handler must see CURRENT wells, not the
  // (usually empty) array from the render the map was created on.
  const wellsRef = useRef<WellDto[]>([]);
  useEffect(() => {
    wellsRef.current = wells;
  }, [wells]);
  const plottedWells = useMemo(
    () => wells.filter((w) => w.lat !== null && w.lon !== null),
    [wells]
  );
  const rigMarkers = useMemo(() => rigsQuery.data?.rigs ?? [], [rigsQuery.data]);
  const freshCount = useMemo(() => rigMarkers.filter((r) => !isStale(r.msgTime)).length, [rigMarkers]);

  const authOptions = useMemo(
    () => (configQuery.data ? mapAuthOptions(configQuery.data) : null),
    [configQuery.data]
  );

  // ── Create the map once auth material is available ──────────────────────────
  useEffect(() => {
    const el = mapElRef.current;
    if (!el || !authOptions || mapRef.current) return;

    const map = new atlas.Map(el, {
      center: [10, 30],
      zoom: 1.4,
      minZoom: 1,
      view: 'Auto',
      style: mapStyle,
      authOptions,
    });
    mapRef.current = map;

    map.events.add('ready', () => {
      const wellsSource = new atlas.source.DataSource();
      const rigsSource = new atlas.source.DataSource();
      map.sources.add([wellsSource, rigsSource]);
      wellsSourceRef.current = wellsSource;
      rigsSourceRef.current = rigsSource;

      // Hollow rendering = transparent fill + colored stroke, via data-driven expressions.
      // The v3 typings only admit literals here (maplibre DataDrivenPropertyValueSpecification),
      // but the SDK evaluates expression arrays fine — hence the through-unknown casts.
      const markerStyle = {
        radius: 6,
        color: ['get', 'color'] as unknown as string,
        opacity: ['case', ['get', 'hollow'], 0, 0.85] as unknown as number,
        strokeColor: ['get', 'color'] as unknown as string,
        strokeWidth: 2,
      };
      const wellsLayer = new atlas.layer.BubbleLayer(wellsSource, undefined, markerStyle);
      const rigsLayer = new atlas.layer.BubbleLayer(rigsSource, undefined, {
        ...markerStyle,
        visible: false,
      });
      map.layers.add([wellsLayer, rigsLayer]);
      wellsLayerRef.current = wellsLayer;
      rigsLayerRef.current = rigsLayer;

      const popup = new atlas.Popup({ pixelOffset: [0, -12], closeButton: false });
      popupRef.current = popup;

      const canvas = map.getCanvasContainer();
      const showPopup = (e: atlas.MapMouseEvent) => {
        const shape = e.shapes?.[0];
        if (!(shape instanceof atlas.Shape)) return;
        const p = shape.getProperties() as Record<string, unknown>;
        const lines = (p.popupLines as { text: string; strong?: boolean; muted?: boolean }[]) ?? [];
        popup.setOptions({
          content: popupContent(lines),
          position: shape.getCoordinates() as atlas.data.Position,
        });
        popup.open(map);
        canvas.style.cursor = 'pointer';
      };
      const hidePopup = () => {
        popup.close();
        canvas.style.cursor = '';
      };

      for (const layer of [wellsLayer, rigsLayer]) {
        map.events.add('mouseover', layer, showPopup);
        map.events.add('mouseout', layer, hidePopup);
      }
      map.events.add('click', wellsLayer, (e) => {
        const shape = e.shapes?.[0];
        if (!(shape instanceof atlas.Shape)) return;
        const p = shape.getProperties() as { wellId?: number };
        setSelectedWell((prev) => {
          const next = wellsRef.current.find((w) => w.id === p.wellId) ?? null;
          return next && prev?.id === next.id ? null : next;
        });
      });
      map.events.add('click', rigsLayer, (e) => {
        const shape = e.shapes?.[0];
        if (!(shape instanceof atlas.Shape)) return;
        const p = shape.getProperties() as { slug?: string };
        if (p.slug) navigate(`/wwd/rigs/${p.slug}`);
      });

      setMapReady(true);
    });

    return () => {
      setMapReady(false);
      mapRef.current = null;
      wellsSourceRef.current = null;
      rigsSourceRef.current = null;
      wellsLayerRef.current = null;
      rigsLayerRef.current = null;
      popupRef.current = null;
      map.dispose();
    };
    // The well-click closure reads `wells` via the dependency below; the map itself is
    // created only once (guarded by mapRef.current).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authOptions]);

  // ── Theme → basemap style (no recreate) ─────────────────────────────────────
  useEffect(() => {
    if (mapReady) mapRef.current?.setStyle({ style: mapStyle });
  }, [mapReady, mapStyle]);

  // ── Well markers ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const source = wellsSourceRef.current;
    if (!mapReady || !source) return;
    source.clear();
    source.add(
      plottedWells.map((w) => {
        const status = WELL_STATUS_LABELS[w.status];
        const operator = w.operator?.name ?? w.operatorNameRaw;
        return new atlas.data.Feature(new atlas.data.Point([w.lon!, w.lat!]), {
          wellId: w.id,
          color: WELL_STATUS_COLORS[w.status],
          hollow: w.approximateCoords,
          popupLines: [
            { text: w.name, strong: true },
            ...(operator ? [{ text: operator }] : []),
            { text: w.approximateCoords ? `${status} · approx. position` : status, muted: true },
          ],
        });
      })
    );
  }, [mapReady, plottedWells]);

  // ── Rig markers ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const source = rigsSourceRef.current;
    if (!mapReady || !source) return;
    source.clear();
    source.add(
      rigMarkers.map((r) => {
        const stale = isStale(r.msgTime);
        const ago = agoLabel(r.msgTime);
        const aisBits = [r.status, ago].filter(Boolean).join(' · ');
        return new atlas.data.Feature(new atlas.data.Point([r.lon, r.lat]), {
          slug: r.slug,
          color: RIG_TYPE_COLORS[r.rigType],
          hollow: stale,
          popupLines: [
            { text: r.name, strong: true },
            { text: `${r.drillco} · ${RIG_TYPE_LABELS[r.rigType]}` },
            ...(aisBits ? [{ text: aisBits, muted: true }] : []),
            { text: `AIS via ${r.source ?? 'aggregator'} (reported)`, muted: true },
          ],
        });
      })
    );
  }, [mapReady, rigMarkers]);

  // ── Tab → layer visibility ───────────────────────────────────────────────────
  useEffect(() => {
    if (!mapReady) return;
    wellsLayerRef.current?.setOptions({ visible: tab === 'wells' });
    rigsLayerRef.current?.setOptions({ visible: tab === 'rigs' });
    popupRef.current?.close();
    if (tab !== 'wells') setSelectedWell(null);
  }, [mapReady, tab]);

  if (configQuery.isLoading) return <LoadingSkeleton lines={8} />;
  if (configQuery.error) return <ErrorState message={(configQuery.error as Error).message} />;

  if (!authOptions) {
    return (
      <Card>
        <CardTitle>Map</CardTitle>
        <CardDescription className="mt-2">
          The map service is not configured yet (no Azure Maps credentials are wired to the
          portal). Wells remain listed under Key Wells.
        </CardDescription>
      </Card>
    );
  }

  return (
    <div className="stagger">
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>{tab === 'wells' ? 'Well Map' : 'Rig Fleet Map'}</CardTitle>
            <CardDescription className="mt-2">
              {tab === 'wells' ? (
                wellsQuery.error ? (
                  <span className="text-accent-warn">
                    Wells failed to load: {(wellsQuery.error as Error).message}
                  </span>
                ) : (
                  <>
                    {plottedWells.length} of {wells.length} tracked wells have public coordinates;
                    the rest are listed under Key Wells only. Hover a dot for details, click to pin
                    them below.
                  </>
                )
              ) : rigsQuery.error ? (
                <span className="text-accent-warn">
                  Rig positions failed to load: {(rigsQuery.error as Error).message}
                </span>
              ) : (
                <>
                  {rigMarkers.length} rigs have a scraped AIS position ({freshCount} fresh,{' '}
                  {rigMarkers.length - freshCount} older than {FRESH_DAYS} days). Positions come
                  from public AIS aggregators and carry <em>reported</em> confidence. Click a
                  marker to open the rig.
                </>
              )}
            </CardDescription>
          </div>
          <div
            className="flex gap-1 rounded-(--radius-sm) border border-border p-1"
            role="tablist"
            aria-label="Map layer"
          >
            {(['wells', 'rigs'] as MapTab[]).map((t) => (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={tab === t}
                onClick={() => setTab(t)}
                className={
                  'rounded-(--radius-sm) px-3 py-1.5 text-sm font-(family-name:--font-ui) transition-colors ' +
                  (tab === t ? 'bg-accent text-white' : 'text-muted hover:text-text')
                }
              >
                {t === 'wells' ? 'Wells' : 'Rigs'}
              </button>
            ))}
          </div>
        </div>
      </Card>

      <Card>
        <div
          ref={mapElRef}
          className="h-[55vh] min-h-[400px] w-full overflow-hidden rounded-(--radius-sm)"
          role="application"
          aria-label={tab === 'wells' ? 'World map of tracked offshore wells' : 'World map of rig AIS positions'}
        />

        {/* Legend */}
        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-border pt-3">
          {tab === 'wells' ? (
            <>
              {(Object.keys(WELL_STATUS_LABELS) as WellStatus[]).map((s) => (
                <span key={s} className="inline-flex items-center gap-1.5 text-xs text-muted">
                  <StatusDot status={s} />
                  {WELL_STATUS_LABELS[s]}
                </span>
              ))}
              <span className="inline-flex items-center gap-1.5 text-xs text-muted">
                <span className="inline-block h-2.5 w-2.5 rounded-full border-[1.5px] border-current" aria-hidden />
                hollow = approximate position (model-derived from well description)
              </span>
            </>
          ) : (
            <>
              {(Object.keys(RIG_TYPE_LABELS) as RigType[]).map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5 text-xs text-muted">
                  <span
                    aria-hidden
                    className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full"
                    style={{ backgroundColor: RIG_TYPE_COLORS[t] }}
                  />
                  {RIG_TYPE_LABELS[t]}
                </span>
              ))}
              <span className="inline-flex items-center gap-1.5 text-xs text-muted">
                <span className="inline-block h-2.5 w-2.5 rounded-full border-[1.5px] border-current" aria-hidden />
                hollow = AIS message older than {FRESH_DAYS} days
              </span>
              {/* NLOD attribution — mandatory wording, must be clearly visible where
                  BarentsWatch-fed positions render (wwd-ais-integration licence note).
                  Live (confirmed) positions cover Norwegian waters only. */}
              <span className="ml-auto inline-flex items-center gap-1 text-xs text-muted">
                Data delivered by{' '}
                <a
                  href="https://www.barentswatch.no/en/services/ais-data/"
                  target="_blank"
                  rel="noreferrer"
                  className="text-accent hover:underline"
                >
                  BarentsWatch
                </a>{' '}
                (Norwegian waters) and vessel-tracking aggregators
              </span>
            </>
          )}
        </div>
      </Card>

      {tab === 'wells' && selectedWell && (
        <WellDetailCard well={selectedWell} onClose={() => setSelectedWell(null)} />
      )}
    </div>
  );
}

/* ---------- pinned well detail (ported from the retired canvas map) ---------- */

function WellDetailCard({ well, onClose }: { well: WellDto; onClose: () => void }) {
  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <div>
          <span className="eyebrow">Well</span>
          <h3 className="mt-2 flex items-center gap-2 text-xl font-semibold text-text">
            <StatusDot status={well.status} />
            {well.name}
            <SourceLink source={well.source} />
            {well.approximateCoords && (
              <span className="rounded-(--radius-sm) border border-accent-warn px-2 py-0.5 font-(family-name:--font-ui) text-[0.65rem] uppercase tracking-[0.12em] text-accent-warn">
                Approx. position
              </span>
            )}
          </h3>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close well details"
          className="rounded-(--radius-sm) p-1 text-muted hover:text-text focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>

      <dl className="mt-4 grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
        <DetailItem label="Country" value={well.country ?? '—'} />
        <DetailItem label="Region" value={well.region?.name ?? '—'} />
        <DetailItem label="Operator" value={well.operator?.name ?? well.operatorNameRaw ?? '—'} />
        <DetailItem label="Status" value={WELL_STATUS_LABELS[well.status]} />
        <DetailItem label="Spud date" value={well.spudDate ?? '—'} />
        <DetailItem
          label="Water depth"
          value={well.waterDepthM !== null ? `${well.waterDepthM.toLocaleString('en-US')} m` : '—'}
        />
      </dl>

      {well.statusRaw && (
        <p className="mt-3 text-sm text-muted">
          <span className="font-(family-name:--font-ui) text-[0.7rem] uppercase tracking-[0.14em]">
            As stated:&nbsp;
          </span>
          {well.statusRaw}
        </p>
      )}

      {well.result && (
        <p className="mt-3 text-sm">
          <span className="font-(family-name:--font-ui) text-[0.7rem] uppercase tracking-[0.14em] text-muted">
            Result:&nbsp;
          </span>
          <ConfidenceValue value={well.result.summary} confidence={well.result.confidence} />{' '}
          <SourceLink source={well.result.source} />
        </p>
      )}
    </Card>
  );
}

function DetailItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="font-(family-name:--font-ui) text-[0.7rem] uppercase tracking-[0.14em] text-muted">
        {label}
      </dt>
      <dd className="mt-0.5 text-text">{value}</dd>
    </div>
  );
}
